import type { RollSource } from './roll-source.js';
import { rollWithStacks } from './weapon-attack-dice.js';
import type { AdvDisTraceEntry, AttackRollRow, RollTuple } from './weapon-attack-commit.js';
import { CRIT_CASCADE_RULES } from './weapon-attack-cascades.js';
import { getSkillTierFromPoints } from '../compile/creature-skill-tier.js';
import contract from '../compile/c1-contract.json';
import type { AbilityUseVerdict } from './ability-use.js';
import { itemsCol as itemField } from '../../shared/array-types.js';

export const FORTITUDE = '24C07918-39D5-4317-A958-BDF68B633A21';
export const DC_ROLL = 'ED5B44EA-7438-9582-A388-04EFF342E460';
export const MAIMED = 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D';
/** Non-trigger status skill ADV/DIS is already compiled; roll-contingent layers need their own executor. */
export function maimedStatusLayersSupported(layers: Record<string, unknown>): boolean {
  return !Object.keys(layers).some(key => key !== 'skill_roll_adv' && key !== 'skill_roll_dis'
    && /^(?:skill_|roll_type_|contesting_roll_|triggers_)/.test(key));
}
export interface FortitudeSaveInput {
  tierDie: number | null;
  untrained: boolean;
  essence: number;
  skillMod: number;
  globalMod: number;
  stacks: number;
  trace: AdvDisTraceEntry[];
  fortune: number;
  /** Null for creatures and untrained PCs; trained PC saves also own the possible ascension draw. */
  ascensionThreshold: number | null;
  ascensionState?: { itemId: string; before: number; total: number; tierLevel: number };
  tierDieForLevel?: (level: number) => number;
}
export interface MaimedSave {
  row: AttackRollRow;
  natural: number;
  failed: boolean;
  unlucky: boolean;
  luckAfter: number;
  ascensionFace: number | null;
  ascension?: { itemId: string; before: number; after: number; totalBefore: number; totalAfter: number; tierBefore: number; tierAfter: number; dc: number };
}

/** Carry a successful save's PC progression into the next hand's Fortitude save. */
export function afterMaimedAscension(input: FortitudeSaveInput, save: MaimedSave | null): FortitudeSaveInput {
  const gain = save?.ascension;
  if (!gain) return input;
  const level = Math.max(input.ascensionState?.tierLevel ?? 0, gain.tierAfter);
  return { ...input, tierDie: input.tierDieForLevel?.(level) ?? level + 3,
    ascensionState: { itemId: gain.itemId, before: gain.after, total: gain.totalAfter, tierLevel: level } };
}

/** Native save/log data; no server skill judgment or second progression read. */
export function maimedAscensionEffects(save: MaimedSave | null, target: string): Extract<AbilityUseVerdict, { kind: 'resolved' }> | null {
  const a = save?.ascension;
  if (!a || save?.ascensionFace == null) return null;
  const gained = a.after > a.before;
  return { kind: 'resolved', ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], inventory: [], deathTail: [], concentrationBreaks: [],
    playerCharactersChanged: gained ? [{ item_id: a.itemId, skill_id: FORTITUDE, column: 'sp_as_fortitude', before: a.before, after: a.after }] : [],
    rows: [{ roll_type_1: gained ? 'gained_ascension_skill_point' : 'failed_roll_for_ascension_skill_point', roll_type_2: '',
      roll_item_id: FORTITUDE, roll_by: target, roll_to: null, roll_result: save.ascensionFace, roll_private: 0,
      rolls_full: [[save.ascensionFace, 'd20_1', { kept: true }], [a.totalAfter, 'sp_total_after']],
      dice: { dice: 1, die: 20, mod: 0, adv_dis_stacks: 0, dc: a.dc } },
      ...(a.tierAfter > a.tierBefore ? [{ roll_type_1: 'ascension_skill_tier_up', roll_type_2: `skill_tier_${a.tierAfter}`,
        roll_item_id: FORTITUDE, roll_by: target, roll_to: null, roll_result: a.tierAfter, roll_private: 0 as const,
        dice: { dice: 0, die: 0, mod: 0, adv_dis_stacks: 0, dc: a.tierBefore } }] : [])],
  };
}

/** Final, revision-admitted receipts only. A late receipt is also inert after the PC has gained more points. */
export function applyMaimedProgression(target: Record<string, unknown>, effects: Pick<Extract<AbilityUseVerdict, { kind: 'resolved' }>, 'playerCharactersChanged'> | undefined): void {
  const pc = target.player_characters as Record<string, unknown> | undefined;
  const creature = (target.computed as { creature?: { skills?: Record<string, Record<string, unknown>> } } | undefined)?.creature;
  const entry = creature?.skills?.fortitude;
  if (!pc || !entry) return;
  for (const change of effects?.playerCharactersChanged ?? []) {
    if (change.column !== 'sp_as_fortitude' || String(itemField(pc, 'item_id') ?? '').toUpperCase() !== change.item_id
        || Number(itemField(pc, 'sp_as_fortitude') ?? 0) !== change.before) continue;
    pc.sp_as_fortitude = change.after;
    const allocated = Number(itemField(pc, 'sp_a_fortitude') ?? 0), training = Number(itemField(pc, 'sp_t_fortitude') ?? 0);
    const total = allocated + training + change.after;
    const level = Math.max(Number(itemField(entry, 'tier_level') ?? 0), getSkillTierFromPoints(total, contract.skillTiers).level);
    entry.tier_level = level;
    entry.raw_sp = { allocated, training, ascension: change.after, total };
    const roll = itemField<Record<string, unknown>>(entry, 'roll');
    if (roll && typeof roll === 'object') roll.die = level + 3;
  }
}

/** skill_roll_arr target save: main d20, tier die, possible ascension d20. No fresh-roll continuation. */
/** skill_roll_arr's is_target walk for the target's universal Evade (lane A8; real-PHP capture janus-ability-oracle-2026-10-02-a8maimed): the
 *  save's effective roll types include the WEAPON's roll type, which Evade's roll_type_against_dis lists — so the target saves at one more DIS:
 *  from the Evade STATUS its pre-roll reaction just applied (live until this save consumes it), or else from the Evade reaction row itself while
 *  it can still fire (triggered_reaction_ability_can_fire: in combat, a reaction left). A spent reaction and no Evade this swing → none. */
export function maimedEvadeSource(evadedThisSwing: boolean, targetReactionLeft: number, targetInCombat: boolean, triggerEnabled = true): AdvDisTraceEntry[] {
  if (!triggerEnabled) return [];
  const kind = evadedThisSwing ? 'status_effect' : (!targetInCombat || targetReactionLeft > 0) ? 'reaction_ability' : null;
  return kind ? [{ delta: -1, source: 'caster_roll_type_against_dis', label: 'Evade', item_id: 'BE4CCA95-33F0-323D-BA54-844D2540C0F0', kind }] : [];
}
export function resolveMaimedSave(roll: RollSource, input: FortitudeSaveInput, luckBefore: number, weaponId: string, against: AdvDisTraceEntry[] = [],
  /** The luck helpers run only while the encounter is in combat (caster_is_unlucky / caster_gains_luck / caster_loses_luck). */
  luckActive = true): MaimedSave {
  const unlucky = luckActive && luckBefore === -1;
  const trace = input.trace.map(e => ({ ...e }));
  if (input.untrained) trace.push({ delta: -1, source: 'untrained', label: 'Untrained', kind: 'skill_training', origin_name: 'Fortitude', origin_item_id: FORTITUDE });
  for (const e of against) trace.push({ ...e });
  if (unlucky) trace.push({ delta: -1, source: 'unlucky', label: 'Unlucky', kind: 'luck' });
  const stacks = input.stacks - Number(input.untrained) - Number(unlucky) + against.reduce((sum, e) => sum + e.delta, 0);
  const main = rollWithStacks(roll, 20, stacks);
  const full: RollTuple[] = [];
  if (stacks === 0) full.push([main.kept, 'd20_1', { kept: true }]);
  else {
    const ordered = main.draws.map((d, i) => ({ value: d.value, order: i + 1 }));
    ordered.sort((a, b) => stacks > 0 ? b.value - a.value : a.value - b.value);
    ordered.forEach((d, i) => full.push([d.value, `d20 ${stacks > 0 ? 'ADV' : 'DIS'}${Math.abs(stacks)}`, { roll_order: d.order, kept: i === 0 }]));
  }
  const tier = input.tierDie === null ? 0 : roll.d(input.tierDie);
  if (input.tierDie !== null) full.push([tier, `Skill Roll d${input.tierDie}`]);
  if (input.globalMod !== 0) full.push([input.globalMod, 'All Skills Mod']);
  if (input.skillMod !== 0) full.push([input.skillMod, 'Skill Roll Mod']);
  full.push([input.essence, 'Body Essence']);
  let luckAfter = unlucky ? 0 : luckBefore;
  if (luckActive && main.kept === 20) luckAfter = Math.min(input.fortune, luckAfter + 1);
  else if (luckActive && main.kept === 1) luckAfter = Math.max(-1, luckAfter - 1);
  const mod = input.essence + input.skillMod + input.globalMod + tier;
  const result = main.kept + mod;
  const rule = CRIT_CASCADE_RULES.maimed;
  trace.push({ log_banner: 'campaign_house_rule', delta: 0, source: 'campaign_house_rule', label: rule.name, kind: 'campaign_house_rule', origin_item_id: rule.id });
  const ascensionFace = input.ascensionThreshold !== null && main.kept >= input.ascensionThreshold ? roll.d(20) : null;
  const state = input.ascensionState;
  const gained = ascensionFace !== null && input.tierDie !== null && ascensionFace >= input.tierDie;
  const totalAfter = (state?.total ?? 0) + Number(gained);
  const ascension = state && ascensionFace !== null ? { itemId: state.itemId, before: state.before, after: state.before + Number(gained),
    totalBefore: state.total, totalAfter, tierBefore: getSkillTierFromPoints(state.total, contract.skillTiers).level,
    tierAfter: getSkillTierFromPoints(totalAfter, contract.skillTiers).level, dc: input.tierDie! } : undefined;
  return { natural: main.kept, failed: result < 15, unlucky, luckAfter, ascensionFace,
    ...(ascension ? { ascension } : {}),
    row: { roll_type_1: DC_ROLL, roll_type_2: FORTITUDE, roll_item_id: weaponId,
      dice: 1, die: 20, mod, adv_dis_stacks: stacks, dc: 15, rolls_full: full, result, adv_dis_trace: trace } };
}
